/**
  OpenShift Dynamic Plugin SDK
  https://github.com/openshift/dynamic-plugin-sdk

  packageName    : @openshift/dynamic-plugin-sdk
  packageVersion : 5.0.1
  buildDate      : January 15, 2024
  buildTime      : 9:29:00 PM GMT+1
  gitCommit      : 1c71da40a72775becd089b345d0e1449513c8c51
  gitBranch      : main
 */

import noop from 'lodash/noop';
import defaultsDeep from 'lodash/defaultsDeep';
import forOwn from 'lodash/forOwn';
import isPlainObject from 'lodash/isPlainObject';
import { v4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import has from 'lodash/has';
import identity from 'lodash/identity';
import * as semver from 'semver';
import * as yup from 'yup';
import * as React from 'react';
import isEqualWith from 'lodash/isEqualWith';

/**
 * Base class for custom errors.
 */
class CustomError extends Error {
    constructor(message) {
        super(message);
        // Set name as constructor name, while keeping its property descriptor compatible with Error.name
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/new.target#new.target_in_constructors
        Object.defineProperty(this, 'name', {
            value: new.target.name,
            configurable: true,
        });
        // Populate stack property via Error.captureStackTrace (when available) or manually via new Error()
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        }
        else {
            this.stack = new Error(message).stack;
        }
    }
}

/**
 * Custom error with a `cause` property.
 *
 * This shouldn't be needed once https://github.com/tc39/proposal-error-cause receives widespread support.
 */
class ErrorWithCause extends CustomError {
    constructor(message, cause) {
        super(message);
        this.cause = cause;
    }
}

const isProdEnv = process.env.NODE_ENV === 'production';
/**
 * {@link Logger} implementation that uses the {@link console} API.
 */
const consoleLogger = {
    /* eslint-disable no-console */
    info: isProdEnv ? noop : console.info,
    warn: console.warn,
    error: console.error,
    /* eslint-enable no-console */
};

/**
 * Create new object by recursively assigning property defaults to `obj`.
 */
const applyDefaults = (obj, defaults) => defaultsDeep({}, obj, defaults);
/**
 * Create new object by recursively assigning property overrides to `obj`.
 */
const applyOverrides = (obj, overrides) => defaultsDeep({}, overrides, obj);
/**
 * Recursive equivalent of Lodash `forOwn` function that traverses objects and arrays.
 */
const visitDeep = (obj, predicate, valueCallback, isObject = (o) => isPlainObject(o)) => {
    forOwn(obj, (value, key, container) => {
        if (predicate(value)) {
            valueCallback(value, key, container);
        }
        else if (isObject(value)) {
            visitDeep(value, predicate, valueCallback, isObject);
        }
        else if (Array.isArray(value)) {
            value.forEach((element) => {
                visitDeep(element, predicate, valueCallback, isObject);
            });
        }
    });
};

const version="5.0.1";

var PluginEventType;
(function (PluginEventType) {
    /**
     * Triggers when the list of extensions, which are currently in use, changes.
     *
     * Associated data getter: {@link PluginStoreInterface.getExtensions}
     */
    PluginEventType["ExtensionsChanged"] = "ExtensionsChanged";
    /**
     * Triggers on changes which have an impact on current plugin information:
     * - plugin was successfully loaded, processed and added to the `PluginStore`
     * - plugin failed to load, or there was an error while processing the plugin
     * - plugin was enabled or disabled (*)
     *
     * (*) this may trigger event {@link PluginEventType.ExtensionsChanged}
     *
     * Associated data getter: {@link PluginStoreInterface.getPluginInfo}
     */
    PluginEventType["PluginInfoChanged"] = "PluginInfoChanged";
    /**
     * Triggers when feature flags have changed.
     *
     * Associated data getter: {@link PluginStoreInterface.getFeatureFlags}
     */
    PluginEventType["FeatureFlagsChanged"] = "FeatureFlagsChanged";
})(PluginEventType || (PluginEventType = {}));

/**
 * Indicates that the given function is a {@link CodeRef} function.
 */
const codeRefSymbol = Symbol('CodeRef');
const isEncodedCodeRef = (obj) => isPlainObject(obj) &&
    isEqual(Object.getOwnPropertyNames(obj), ['$codeRef']) &&
    typeof obj.$codeRef === 'string';
const isCodeRef = (obj) => typeof obj === 'function' &&
    isEqual(Object.getOwnPropertySymbols(obj), [codeRefSymbol]) &&
    obj[codeRefSymbol] === true;
/**
 * Parse data from the {@link EncodedCodeRef} object.
 *
 * Returns `undefined` if the `$codeRef` value is malformed.
 */
const parseEncodedCodeRef = (ref) => {
    const match = ref.$codeRef.match(/^([^.\s]+)(?:\.([^.\s]+))?$/);
    if (!match) {
        return undefined;
    }
    const moduleName = match[1];
    const exportName = match[2] || 'default';
    return [moduleName, exportName];
};
const getPluginModule = async (moduleName, entryModule, formatErrorMessage = identity) => {
    try {
        const moduleFactory = await entryModule.get(moduleName);
        return moduleFactory();
    }
    catch (e) {
        throw new ErrorWithCause(formatErrorMessage(`Failed to load module '${moduleName}'`), e);
    }
};
/**
 * Create new {@link CodeRef} function from an {@link EncodedCodeRef} object.
 */
const createCodeRef = (encodedCodeRef, entryModule, formatErrorMessage = identity) => async () => {
    const refData = parseEncodedCodeRef(encodedCodeRef);
    if (!refData) {
        throw new Error(formatErrorMessage(`Malformed code reference '${encodedCodeRef.$codeRef}'`));
    }
    const [moduleName, exportName] = refData;
    const referencedModule = await getPluginModule(moduleName, entryModule, formatErrorMessage);
    if (!has(referencedModule, exportName)) {
        throw new Error(formatErrorMessage(`Missing module export '${moduleName}.${exportName}'`));
    }
    return referencedModule[exportName];
};
/**
 * In the extension's `properties` object, replace all {@link EncodedCodeRef} values
 * with {@link CodeRef} functions that can be used to load the referenced objects.
 *
 * Returns the extension that was passed in.
 */
const decodeCodeRefs = (extension, entryModule) => {
    visitDeep(extension.properties, isEncodedCodeRef, (encodedCodeRef, key, obj) => {
        const codeRef = createCodeRef(encodedCodeRef, entryModule, (message) => `${message} in extension ${extension.uid}`);
        // Use a sensible function name that reflects the current context
        Object.defineProperty(codeRef, 'name', {
            value: `$codeRef_${extension.pluginName}[${encodedCodeRef.$codeRef}]`,
            configurable: true,
        });
        // Mark the function with a non-configurable symbol property
        Object.defineProperty(codeRef, codeRefSymbol, { value: true });
        // eslint-disable-next-line no-param-reassign
        obj[key] = codeRef;
    });
    return extension;
};
/**
 * In the extension's `properties` object, replace all {@link CodeRef} functions with
 * their corresponding values by resolving the associated Promises.
 *
 * This is an asynchronous operation that completes when all of the associated Promises
 * are either resolved or rejected. Each code reference resolution error will cause the
 * associated property value to be set to `undefined`.
 *
 * The resulting Promise resolves with a new extension instance; its `properties` object
 * is cloned in order to preserve the original extension.
 *
 * The resulting Promise never rejects. Use the `onResolutionErrors` callback to handle
 * any code reference resolution errors.
 */
const resolveCodeRefValues = async (extension, onResolutionErrors) => {
    const clonedProperties = cloneDeep(extension.properties);
    const resolutions = [];
    const resolutionErrors = [];
    visitDeep(clonedProperties, isCodeRef, (codeRef, key, obj) => {
        resolutions.push(codeRef()
            // eslint-disable-next-line promise/always-return -- resolutions element type is Promise<void>
            .then((resolvedValue) => {
            // eslint-disable-next-line no-param-reassign
            obj[key] = resolvedValue;
        })
            .catch((e) => {
            resolutionErrors.push(e);
            // eslint-disable-next-line no-param-reassign
            obj[key] = undefined;
        }));
    });
    await Promise.allSettled(resolutions);
    if (resolutionErrors.length > 0) {
        onResolutionErrors(resolutionErrors);
    }
    return {
        ...extension,
        properties: clonedProperties,
    };
};

const DEFAULT_REMOTE_ENTRY_CALLBACK = '__load_plugin_entry__';

class FetchError extends CustomError {
    constructor(message, status, response) {
        super(message);
        this.status = status;
        this.response = response;
    }
}
/**
 * Basic implementation of {@link ResourceFetch} that uses the {@link fetch} API.
 */
const basicFetch = async (url, requestInit = {}) => {
    const response = await fetch(url, applyDefaults(requestInit, { method: 'GET' }));
    if (!response.ok) {
        throw new FetchError(response.statusText, response.status, response);
    }
    return response;
};

const isPromiseFulfilledResult = (r) => r.status === 'fulfilled';
const isPromiseRejectedResult = (r) => r.status === 'rejected';
/**
 * Unwrap the results of `Promise.allSettled()` call for easier processing.
 */
const unwrapPromiseSettledResults = (results) => [
    results.filter(isPromiseFulfilledResult).map((r) => r.value),
    results.filter(isPromiseRejectedResult).map((r) => r.reason),
];
/**
 * Await `Promise.allSettled(promises)` and unwrap the results.
 *
 * Note that the Promise returned by `Promise.allSettled()` never rejects.
 */
const settleAllPromises = async (promises) => {
    const results = await Promise.allSettled(promises);
    return unwrapPromiseSettledResults(results);
};

/**
 * Load a script from the given URL by injecting new HTML `script` element into the document.
 */
const injectScriptElement = (url, id, getDocument = () => document) => new Promise((resolve, reject) => {
    const script = getDocument().createElement('script');
    script.async = true;
    script.src = url;
    script.id = id;
    script.onload = () => {
        resolve();
    };
    script.onerror = (event) => {
        reject(event);
    };
    getDocument().head.appendChild(script);
});
/**
 * Get the corresponding HTML `script` element or `null` if not present in the document.
 */
const getScriptElement = (id, getDocument = () => document) => Array.from(getDocument().scripts).find((script) => script.id === id) ?? null;

/**
 * Return `true` if the given URL is absolute.
 *
 * @see https://stackoverflow.com/a/38979205
 */
const isAbsoluteURL = (url) => url.indexOf('://') > 0 || url.indexOf('//') === 0;
/**
 * Resolve URL to the given resource using a base URL.
 *
 * If `base` is not an absolute URL, it's considered to be relative to the document origin.
 *
 * If `to` is an absolute URL, `base` is ignored (as per standard {@link URL} constructor semantics).
 */
const resolveURL = (base, to, processURL = identity, getDocumentOrigin = () => window.location.origin) => processURL(new URL(to, isAbsoluteURL(base) ? base : new URL(base, getDocumentOrigin()))).toString();

// TODO(vojtech): suppress false positive https://github.com/jsx-eslint/eslint-plugin-react/pull/3326
/**
 * Schema for a valid semver string.
 *
 * @see https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
 */
const semverStringSchema = yup
    .string()
    .required()
    .matches(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/);
/**
 * Schema for a valid plugin name.
 *
 * @example
 * ```
 * foo
 * foo-bar
 * foo.bar
 * foo.bar-Test
 * Foo-Bar-abc.123
 * ```
 */
const pluginNameSchema = yup
    .string()
    .required()
    .matches(/^[a-zA-Z]+(?:[-.]?[a-zA-Z0-9]+)*$/);
/**
 * Schema for a valid extension type.
 *
 * @example
 * ```
 * app.foo
 * app.foo-bar
 * app.foo/bar
 * My-app.Foo-Bar
 * My-app.Foo-Bar/abcTest
 * ```
 */
const extensionTypeSchema = yup
    .string()
    .required()
    .matches(/^[a-zA-Z]+(?:-[a-zA-Z]+)*\.[a-zA-Z]+(?:-[a-zA-Z]+)*(?:\/[a-zA-Z]+(?:-[a-zA-Z]+)*)*$/);
/**
 * Schema for a valid feature flag name.
 *
 * @example
 * ```
 * FOO
 * FOO_BAR
 * FOO_BAR123
 * ```
 */
const featureFlagNameSchema = yup
    .string()
    .required()
    .matches(/^[A-Z]+[A-Z0-9_]*$/);
/**
 * Schema for `Extension` objects.
 */
const extensionSchema = yup
    .object()
    .required()
    .shape({
    type: extensionTypeSchema,
    properties: yup.object().required(),
    flags: yup.object().shape({
        required: yup.array().of(featureFlagNameSchema),
        disallowed: yup.array().of(featureFlagNameSchema),
    }),
});
/**
 * Schema for an array of `Extension` objects.
 */
const extensionArraySchema = yup.array().of(extensionSchema).required();
/**
 * Schema for `PluginRegistrationMethod` objects.
 */
const pluginRegistrationMethodSchema = yup
    .mixed()
    .oneOf(['callback', 'custom'])
    .required();
/**
 * Schema for `PluginRuntimeMetadata` objects.
 */
const pluginRuntimeMetadataSchema = yup.object().required().shape({
    name: pluginNameSchema,
    version: semverStringSchema,
    // TODO(vojtech): Yup lacks native support for map-like structures with arbitrary keys
    // TODO(vojtech): we need to validate dependency values as semver ranges
    dependencies: yup.object(),
    customProperties: yup.object(),
});
/**
 * Schema for `PluginManifest` objects.
 */
const pluginManifestSchema = pluginRuntimeMetadataSchema.shape({
    baseURL: yup.string().required(),
    extensions: extensionArraySchema,
    loadScripts: yup.array().of(yup.string().required()).required(),
    registrationMethod: pluginRegistrationMethodSchema,
    buildHash: yup.string(),
});

/**
 * Loads plugin assets from remote sources.
 */
class PluginLoader {
    constructor(options = {}) {
        /** Plugins processed by this loader. */
        this.plugins = new Map();
        this.loadListeners = new Set();
        this.options = {
            canLoadPlugin: options.canLoadPlugin ?? (() => true),
            canReloadScript: options.canReloadScript ?? (() => true),
            entryCallbackSettings: options.entryCallbackSettings ?? {},
            fetchImpl: options.fetchImpl ?? basicFetch,
            fixedPluginDependencyResolutions: options.fixedPluginDependencyResolutions ?? {},
            sharedScope: options.sharedScope ?? {},
            transformPluginManifest: options.transformPluginManifest ?? identity,
            getPluginEntryModule: options.getPluginEntryModule ?? noop,
        };
        this.registerPluginEntryCallback();
    }
    invokeLoadListeners() {
        this.loadListeners.forEach((listener) => {
            listener();
        });
    }
    async loadPluginManifest(manifestURL) {
        const response = await this.options.fetchImpl(manifestURL, { cache: 'no-cache' });
        const manifest = JSON.parse(await response.text());
        pluginManifestSchema.validateSync(manifest, { strict: true, abortEarly: false });
        return manifest;
    }
    transformPluginManifest(manifest) {
        return this.options.transformPluginManifest(manifest);
    }
    /**
     * @inheritDoc
     *
     * Plugins using the `callback` registration method are expected to call the global entry
     * callback function created via {@link registerPluginEntryCallback} method, passing two
     * arguments: plugin name and the entry module.
     *
     * For plugins using the `custom` registration method, the `getPluginEntryModule` function
     * is expected to return the entry module of the given plugin. If not implemented properly,
     * plugins using the `custom` registration method will fail to load.
     */
    async loadPlugin(manifest) {
        const pluginName = manifest.name;
        const reload = this.plugins.has(pluginName);
        const data = { status: 'pending', manifest };
        let entryModule;
        if (manifest.registrationMethod === 'callback') {
            data.entryCallbackFired = false;
        }
        this.plugins.set(pluginName, data);
        if (!this.options.canLoadPlugin(manifest, reload)) {
            data.status = 'failed';
            this.invokeLoadListeners();
            return {
                success: false,
                errorMessage: `Plugin ${pluginName} is not allowed to be ${reload ? 'reloaded' : 'loaded'}`,
            };
        }
        try {
            await this.resolvePluginDependencies(manifest);
        }
        catch (e) {
            data.status = 'failed';
            this.invokeLoadListeners();
            return {
                success: false,
                errorMessage: `Failed to resolve dependencies of plugin ${pluginName}`,
                errorCause: e,
            };
        }
        try {
            await this.loadPluginScripts(manifest, data);
        }
        catch (e) {
            data.status = 'failed';
            this.invokeLoadListeners();
            return {
                success: false,
                errorMessage: `Failed to load scripts of plugin ${pluginName}`,
                errorCause: e,
            };
        }
        try {
            entryModule = await this.initSharedModules(manifest, data);
        }
        catch (e) {
            data.status = 'failed';
            this.invokeLoadListeners();
            return {
                success: false,
                errorMessage: `Failed to initialize shared modules of plugin ${pluginName}`,
                errorCause: e,
            };
        }
        data.status = 'loaded';
        this.invokeLoadListeners();
        return { success: true, entryModule };
    }
    /**
     * Load all scripts of the given plugin.
     */
    async loadPluginScripts(manifest, data) {
        const pluginName = manifest.name;
        const [, rejectedReasons] = await settleAllPromises(manifest.loadScripts.map((scriptName) => {
            const scriptID = `${pluginName}/${scriptName}`;
            const scriptElement = getScriptElement(scriptID);
            if (scriptElement && !this.options.canReloadScript(manifest, scriptName)) {
                return Promise.resolve();
            }
            if (scriptElement) {
                scriptElement.remove();
            }
            const scriptURL = resolveURL(manifest.baseURL, scriptName, (url) => {
                url.searchParams.set('cacheBuster', v4());
                return url;
            });
            return injectScriptElement(scriptURL, scriptID);
        }));
        if (rejectedReasons.length > 0) {
            throw new ErrorWithCause('Detected errors while loading scripts', rejectedReasons);
        }
        if (manifest.registrationMethod === 'callback' && !data.entryCallbackFired) {
            throw new Error('Scripts loaded without a plugin entry callback');
        }
        if (manifest.registrationMethod === 'callback' && !data.entryCallbackModule) {
            throw new Error('Plugin entry callback called without an entry module');
        }
    }
    /**
     * Initialize the plugin with provided shared modules.
     */
    async initSharedModules(manifest, data) {
        const pluginName = manifest.name;
        const entryModule = manifest.registrationMethod === 'callback'
            ? data.entryCallbackModule
            : this.options.getPluginEntryModule(manifest);
        if (!entryModule) {
            throw new Error(`Failed to retrieve entry module of plugin ${pluginName}`);
        }
        if (typeof entryModule.init !== 'function' || typeof entryModule.get !== 'function') {
            throw new Error(`Entry module of plugin ${pluginName} does not meet expected contract`);
        }
        await Promise.resolve(entryModule.init(this.options.sharedScope));
        return entryModule;
    }
    getCurrentDependencyResolutions() {
        const resolutions = new Map();
        Array.from(this.plugins.entries()).forEach(([pluginName, data]) => {
            if (data.status === 'loaded') {
                resolutions.set(pluginName, { success: true, version: data.manifest.version });
            }
            else if (data.status === 'failed') {
                resolutions.set(pluginName, { success: false });
            }
        });
        Object.entries(this.options.fixedPluginDependencyResolutions).forEach(([depName, version]) => {
            if (semver.valid(version)) {
                resolutions.set(depName, { success: true, version });
            }
        });
        return resolutions;
    }
    /**
     * Resolve all dependencies of the given plugin.
     *
     * Fail early if there are any unsuccessful or unmet dependency resolutions.
     */
    resolvePluginDependencies(manifest) {
        return new Promise((resolve, reject) => {
            const pluginName = manifest.name;
            const dependencies = manifest.dependencies ?? {};
            const semverRangeOptions = { includePrerelease: true };
            let isResolutionComplete = false;
            let listener;
            const setResolutionComplete = () => {
                isResolutionComplete = true;
                this.loadListeners.delete(listener);
            };
            const tryResolveDependencies = () => {
                const resolutions = this.getCurrentDependencyResolutions();
                const resolutionErrors = [];
                Object.entries(dependencies).forEach(([depName, versionRange]) => {
                    if (resolutions.has(depName)) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        const res = resolutions.get(depName);
                        if (res.success && !semver.satisfies(res.version, versionRange, semverRangeOptions)) {
                            resolutionErrors.push(`Dependency ${depName} not met: required range ${versionRange}, resolved version ${res.version}`);
                        }
                        else if (!res.success) {
                            resolutionErrors.push(`Dependency ${depName} could not be resolved successfully`);
                        }
                    }
                });
                if (resolutionErrors.length > 0) {
                    setResolutionComplete();
                    reject(new ErrorWithCause('Detected dependency resolution errors', resolutionErrors));
                    return;
                }
                const pendingDepNames = Object.keys(dependencies).filter((depName) => !resolutions.has(depName));
                if (pendingDepNames.length === 0) {
                    setResolutionComplete();
                    resolve();
                }
                else {
                    consoleLogger.info(`Plugin ${pluginName} has ${pendingDepNames.length} pending dependency resolutions`);
                }
            };
            tryResolveDependencies();
            if (!isResolutionComplete) {
                listener = tryResolveDependencies;
                this.loadListeners.add(listener);
            }
        });
    }
    createPluginEntryCallback() {
        return (pluginName, entryModule) => {
            if (!this.plugins.has(pluginName)) {
                consoleLogger.warn(`Received entry callback for unknown plugin ${pluginName}`);
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const data = this.plugins.get(pluginName);
            if (data.entryCallbackFired) {
                consoleLogger.warn(`Entry callback for plugin ${pluginName} called more than once`);
                return;
            }
            data.entryCallbackFired = true;
            data.entryCallbackModule = entryModule;
        };
    }
    /**
     * Register the global callback function used by plugin entry scripts.
     *
     * This must be called in order to load plugins using the `callback` registration method.
     */
    registerPluginEntryCallback() {
        const registerCallback = this.options.entryCallbackSettings.registerCallback ?? true;
        const callbackName = this.options.entryCallbackSettings.name ?? DEFAULT_REMOTE_ENTRY_CALLBACK;
        if (!registerCallback) {
            consoleLogger.info(`Plugin entry callback ${callbackName} will not be registered`);
            return;
        }
        if (typeof window[callbackName] === 'function') {
            consoleLogger.warn(`Plugin entry callback ${callbackName} is already registered`);
            return;
        }
        window[callbackName] = this.createPluginEntryCallback();
    }
}

/**
 * Manages plugins and their extensions.
 */
class PluginStore {
    constructor(options = {}) {
        this.pendingPromises = new Map();
        /** Plugins that are currently being loaded. */
        this.pendingPlugins = new Map();
        /** Plugins that were successfully loaded and processed. */
        this.loadedPlugins = new Map();
        /** Plugins that failed to load or get processed properly. */
        this.failedPlugins = new Map();
        /** Extensions which are currently in use. */
        this.extensions = [];
        /** Subscribed event listeners. */
        this.listeners = new Map();
        /** Feature flags used to determine the availability of extensions. */
        this.featureFlags = {};
        this.sdkVersion = version;
        this.options = {
            autoEnableLoadedPlugins: options.autoEnableLoadedPlugins ?? true,
        };
        this.loader = options.loader ?? new PluginLoader(options.loaderOptions);
        Object.values(PluginEventType).forEach((t) => {
            this.listeners.set(t, new Set());
        });
    }
    subscribe(eventTypes, listener) {
        let isSubscribed = true;
        if (eventTypes.length === 0) {
            consoleLogger.warn('subscribe method called with empty eventTypes');
            return noop;
        }
        eventTypes.forEach((t) => {
            this.listeners.get(t)?.add(listener);
        });
        return () => {
            if (isSubscribed) {
                isSubscribed = false;
                eventTypes.forEach((t) => {
                    this.listeners.get(t)?.delete(listener);
                });
            }
        };
    }
    invokeListeners(eventType) {
        this.listeners.get(eventType)?.forEach((listener) => {
            listener();
        });
    }
    getExtensions() {
        return [...this.extensions];
    }
    getPluginInfo() {
        const entries = [];
        Array.from(this.pendingPlugins.values()).forEach((plugin) => {
            entries.push({
                status: 'pending',
                manifest: plugin.manifest,
            });
        });
        Array.from(this.loadedPlugins.values()).forEach((plugin) => {
            entries.push({
                status: 'loaded',
                manifest: plugin.manifest,
                enabled: plugin.enabled,
                disableReason: plugin.disableReason,
            });
        });
        Array.from(this.failedPlugins.values()).forEach((plugin) => {
            entries.push({
                status: 'failed',
                manifest: plugin.manifest,
                errorMessage: plugin.errorMessage,
                errorCause: plugin.errorCause,
            });
        });
        return entries;
    }
    getFeatureFlags() {
        return { ...this.featureFlags };
    }
    setFeatureFlags(newFlags) {
        const prevFeatureFlags = this.featureFlags;
        this.featureFlags = {
            ...this.featureFlags,
            ...pickBy(newFlags, (value) => typeof value === 'boolean'),
        };
        if (!isEqual(prevFeatureFlags, this.featureFlags)) {
            this.updateExtensions();
            this.invokeListeners(PluginEventType.FeatureFlagsChanged);
        }
    }
    async loadPlugin(manifest, forceReload) {
        let loadedManifest;
        try {
            loadedManifest =
                typeof manifest === 'string' ? await this.loader.loadPluginManifest(manifest) : manifest;
        }
        catch (e) {
            throw new ErrorWithCause('Failed to load plugin manifest', e);
        }
        loadedManifest = this.loader.transformPluginManifest(loadedManifest);
        const pluginName = loadedManifest.name;
        if (this.pendingPlugins.has(pluginName)) {
            return this.pendingPromises.get(pluginName);
        }
        if (this.loadedPlugins.has(pluginName) && !forceReload) {
            return Promise.resolve();
        }
        this.addPendingPlugin(loadedManifest);
        consoleLogger.info(`Loading plugin ${pluginName} version ${loadedManifest.version}`);
        const promise = (async () => {
            const result = await this.loader.loadPlugin(loadedManifest);
            if (result.success) {
                this.addLoadedPlugin(loadedManifest, result.entryModule);
                consoleLogger.info(`Plugin ${pluginName} has been loaded`);
                if (this.options.autoEnableLoadedPlugins) {
                    this.enablePlugins([pluginName]);
                }
            }
            else {
                const { errorMessage, errorCause } = result;
                this.addFailedPlugin(loadedManifest, errorMessage, errorCause);
                consoleLogger.error(`Plugin ${pluginName} has failed to load`, ...compact([errorMessage, errorCause]));
            }
            this.pendingPromises.delete(pluginName);
        })();
        this.pendingPromises.set(pluginName, promise);
        return promise;
    }
    setPluginsEnabled(pluginNames, enabled, onEnabledChange) {
        let updateRequired = false;
        pluginNames.forEach((pluginName) => {
            if (!this.loadedPlugins.has(pluginName)) {
                consoleLogger.warn(`Attempt to ${enabled ? 'enable' : 'disable'} plugin ${pluginName} which is not currently loaded`);
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const plugin = this.loadedPlugins.get(pluginName);
            if (plugin.enabled !== enabled) {
                plugin.enabled = enabled;
                onEnabledChange(plugin);
                updateRequired = true;
                consoleLogger.info(`Plugin ${pluginName} will be ${enabled ? 'enabled' : 'disabled'}`);
            }
        });
        if (updateRequired) {
            this.updateExtensions();
            this.invokeListeners(PluginEventType.PluginInfoChanged);
        }
    }
    enablePlugins(pluginNames) {
        this.setPluginsEnabled(pluginNames, true, (plugin) => {
            // eslint-disable-next-line no-param-reassign
            plugin.disableReason = undefined;
        });
    }
    disablePlugins(pluginNames, disableReason) {
        this.setPluginsEnabled(pluginNames, false, (plugin) => {
            // eslint-disable-next-line no-param-reassign
            plugin.disableReason = disableReason;
        });
    }
    isExtensionInUse(extension) {
        return ((extension.flags?.required?.every((f) => this.featureFlags[f] === true) ?? true) &&
            (extension.flags?.disallowed?.every((f) => this.featureFlags[f] === false) ?? true));
    }
    updateExtensions() {
        const prevExtensions = this.extensions;
        this.extensions = Array.from(this.loadedPlugins.values()).reduce((acc, p) => p.enabled ? [...acc, ...p.loadedExtensions.filter((e) => this.isExtensionInUse(e))] : acc, []);
        if (!isEqual(prevExtensions, this.extensions)) {
            this.invokeListeners(PluginEventType.ExtensionsChanged);
        }
    }
    addPendingPlugin(manifest) {
        const pluginName = manifest.name;
        this.pendingPlugins.set(pluginName, { manifest });
        this.loadedPlugins.delete(pluginName);
        this.failedPlugins.delete(pluginName);
        this.invokeListeners(PluginEventType.PluginInfoChanged);
        this.updateExtensions();
    }
    /**
     * Add a loaded plugin to the {@link PluginStore}.
     *
     * Once added, the plugin is disabled by default. Enable it to put its extensions into use.
     */
    addLoadedPlugin(manifest, entryModule) {
        const pluginName = manifest.name;
        const buildHash = manifest.buildHash ?? v4();
        const loadedExtensions = cloneDeep(manifest.extensions).map((e, index) => decodeCodeRefs({
            ...e,
            pluginName,
            uid: `${pluginName}[${index}]_${buildHash}`,
        }, entryModule));
        const loadedPlugin = {
            // TODO(vojtech): use deepFreeze on the manifest and type it as DeepReadonly
            manifest: Object.freeze(manifest),
            loadedExtensions: loadedExtensions.map((e) => Object.freeze(e)),
            entryModule,
            enabled: false,
        };
        this.pendingPlugins.delete(pluginName);
        this.loadedPlugins.set(pluginName, loadedPlugin);
        this.failedPlugins.delete(pluginName);
        this.invokeListeners(PluginEventType.PluginInfoChanged);
        this.updateExtensions();
    }
    addFailedPlugin(manifest, errorMessage, errorCause) {
        const pluginName = manifest.name;
        this.pendingPlugins.delete(pluginName);
        this.loadedPlugins.delete(pluginName);
        this.failedPlugins.set(pluginName, { manifest, errorMessage, errorCause });
        this.invokeListeners(PluginEventType.PluginInfoChanged);
        this.updateExtensions();
    }
    async getExposedModule(pluginName, moduleName) {
        if (!this.loadedPlugins.has(pluginName)) {
            throw new Error(`Attempt to get module '${moduleName}' of plugin ${pluginName} which is not currently loaded`);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const plugin = this.loadedPlugins.get(pluginName);
        const referencedModule = await getPluginModule(moduleName, plugin.entryModule, (message) => `${message} of plugin ${pluginName}`);
        return referencedModule;
    }
}

const PluginStoreContext = React.createContext(undefined);
/**
 * React Context provider for passing the {@link PluginStore} down the component tree.
 */
const PluginStoreProvider = ({ store, children }) => (React.createElement(PluginStoreContext.Provider, { value: store }, children));
/**
 * React hook that provides access to the {@link PluginStore} functionality.
 */
const usePluginStore = () => {
    const store = React.useContext(PluginStoreContext);
    if (store === undefined) {
        throw new Error('usePluginStore hook called outside a PluginStoreProvider');
    }
    return store;
};

const isSameReference = (a, b) => a === b;
/**
 * React hook for subscribing to `PluginStore` events.
 *
 * This hook implements the common `PluginStore` usage pattern, returning the current
 * data via the `getData` function.
 *
 * @example
 * ```
 * pluginStore.subscribe(eventTypes, () => {
 *   // get current data from plugin store
 *   // compare current data with previous data
 *   // re-render the component on data change
 * });
 * ```
 *
 * Return value changes only if both of the following conditions are true:
 * - `PluginStore` has emitted events of interest
 * - current data has changed, compared to previously referenced data
 *
 * The referential stability of the hook's result depends on a stable implementation
 * of `isSameData` function. If not specified, the hook performs a reference equality
 * comparison.
 */
const usePluginSubscription = (eventTypes, getData, isSameData = isSameReference) => {
    const pluginStore = usePluginStore();
    const getDataRef = React.useRef(getData);
    getDataRef.current = getData;
    const isSameDataRef = React.useRef(isSameData);
    isSameDataRef.current = isSameData;
    const [hookResult, setHookResult] = React.useState(() => getData(pluginStore));
    const updateResult = React.useCallback(() => {
        const nextData = getDataRef.current(pluginStore);
        setHookResult((prevData) => (isSameDataRef.current(prevData, nextData) ? prevData : nextData));
    }, [pluginStore]);
    React.useEffect(() => updateResult(), [getData, isSameData, updateResult]);
    React.useEffect(() => pluginStore.subscribe(eventTypes, updateResult), [pluginStore, eventTypes, updateResult]);
    return hookResult;
};

const eventTypes$2 = [PluginEventType.ExtensionsChanged];
const getData$1 = (pluginStore) => pluginStore.getExtensions();
const isSameData$1 = (prevData, nextData) => isEqualWith(prevData, nextData, (a, b) => a === b);
/**
 * React hook for consuming extensions which are currently in use.
 *
 * The optional `predicate` parameter may be used to filter resulting extensions.
 *
 * This hook re-renders the component whenever the list of matching extensions changes.
 *
 * The hook's result is guaranteed to be referentially stable across re-renders.
 */
const useExtensions = (predicate) => {
    const extensions = usePluginSubscription(eventTypes$2, getData$1, isSameData$1);
    return React.useMemo(() => extensions.reduce((acc, e) => ((predicate ?? (() => true))(e) ? [...acc, e] : acc), []), [extensions, predicate]);
};

/**
 * React hook that calls `useExtensions` and resolves all code references in all matching extensions.
 *
 * Resolving code references to their corresponding values is an asynchronous operation. Initially,
 * this hook returns a pending result tuple `[resolvedExtensions: [], resolved: false, errors: []]`.
 *
 * Once the resolution is complete, this hook re-renders the component with a result tuple containing
 * all matching extensions that had their code references resolved successfully along with any errors
 * that occurred during the process.
 *
 * This hook supports an options argument to customize its default behavior.
 *
 * When the list of matching extensions changes, the resolution is restarted.
 *
 * The hook's result tuple elements are guaranteed to be referentially stable across re-renders.
 *
 * @see {@link useExtensions}
 */
const useResolvedExtensions = (predicate, options = {}) => {
    const includeExtensionsWithResolutionErrors = options.includeExtensionsWithResolutionErrors ?? false;
    const extensions = useExtensions(predicate);
    const [resolvedExtensions, setResolvedExtensions] = React.useState([]);
    const [resolved, setResolved] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    React.useEffect(() => {
        const allResolutionErrors = [];
        const failedExtensionUIDs = [];
        // eslint-disable-next-line promise/catch-or-return -- this Promise never rejects
        settleAllPromises(extensions.map((e) => resolveCodeRefValues(e, (resolutionErrors) => {
            allResolutionErrors.push(...resolutionErrors);
            failedExtensionUIDs.push(e.uid);
        }))).then(([fulfilledValues]) => {
            if (allResolutionErrors.length > 0) {
                consoleLogger.error('useResolvedExtensions has detected code reference resolution errors', allResolutionErrors);
            }
            // eslint-disable-next-line promise/always-return -- this Promise is handled inline
            const resultExtensions = includeExtensionsWithResolutionErrors
                ? fulfilledValues
                : fulfilledValues.filter((e) => !failedExtensionUIDs.includes(e.uid));
            setResolved(true);
            setResolvedExtensions(resultExtensions);
            setErrors(allResolutionErrors);
        });
        return () => {
            setResolved(false);
            setResolvedExtensions([]);
            setErrors([]);
        };
    }, [extensions, includeExtensionsWithResolutionErrors]);
    return [resolvedExtensions, resolved, errors];
};

const eventTypes$1 = [PluginEventType.PluginInfoChanged];
const getData = (pluginStore) => pluginStore.getPluginInfo();
/**
 * React hook for consuming current information about plugins.
 *
 * This hook re-renders the component whenever the plugin information changes.
 *
 * The hook's result is guaranteed to be referentially stable across re-renders.
 */
const usePluginInfo = () => {
    return usePluginSubscription(eventTypes$1, getData, isEqual);
};

const eventTypes = [PluginEventType.FeatureFlagsChanged];
const isSameData = (prevData, nextData) => prevData === nextData;
/**
 * React hook that provides access to a feature flag.
 *
 * This hook re-renders the component whenever the value of the given flag is updated.
 *
 * The hook's result is guaranteed to be referentially stable across re-renders.
 *
 * @example
 * ```ts
 * const [flag, setFlag] = useFeatureFlag('FOO');
 * setFlag(true);
 * ```
 */
const useFeatureFlag = (name) => {
    const getData = React.useCallback((pluginStore) => pluginStore.getFeatureFlags()[name], [name]);
    const currentValue = usePluginSubscription(eventTypes, getData, isSameData);
    const pluginStore = usePluginStore();
    const setValue = React.useCallback((value) => {
        pluginStore.setFeatureFlags({ [name]: value });
    }, [pluginStore, name]);
    return [currentValue, setValue];
};

/**
 * `PluginStore` implementation intended for testing purposes.
 */
class TestPluginStore extends PluginStore {
    addPendingPlugin(manifest) {
        super.addPendingPlugin(manifest);
    }
    addLoadedPlugin(manifest, entryModule) {
        super.addLoadedPlugin(manifest, entryModule);
    }
    addFailedPlugin(manifest, errorMessage, errorCause) {
        super.addFailedPlugin(manifest, errorMessage, errorCause);
    }
}

export { CustomError, PluginEventType, PluginStore, PluginStoreProvider, TestPluginStore, applyDefaults, applyOverrides, consoleLogger, useExtensions, useFeatureFlag, usePluginInfo, usePluginStore, useResolvedExtensions };
